import { useCallback, useEffect, useRef } from "react";
import { BASE_API_URL } from "../config";

/** How long the drawing has to stop before it is written. */
const SAVE_PAUSE_MS = 1400;

/**
 * What a student has drawn over a paper, kept between visits.
 *
 * The document is whatever the caller wants stored — here, one board per
 * question, so ink drawn beside question 1 does not follow you to 2.
 *
 * The board is read and written whole, which is how it is stored: a paper's
 * working is wanted all at once when it opens, and a stroke is meaningless
 * on its own. Writes wait for the drawing to pause, so a line is one request
 * rather than one per point.
 *
 * The revision the client last read goes back with every save. A student
 * with the same paper open twice would otherwise lose whichever tab saved
 * first; instead the second save is refused and this reloads rather than
 * overwriting work it never saw.
 */
export function usePaperAnnotations({ attemptGuid, document, onLoaded, enabled = true }) {
  const revision = useRef(0);
  const timer = useRef(null);
  const lastSent = useRef(null);
  const loaded = useRef(false);
  // Set when a document arrives from the server: the next document this
  // hook sees is that one being put out, not an edit, so it is taken as the
  // starting point rather than written straight back. Writing it back cost
  // a revision for nothing — and with a collaborator on the same paper, it
  // made their next save look stale.
  const fromServer = useRef(false);
  // Held in a ref so a caller may pass a fresh closure without restarting
  // the load.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const token = typeof localStorage !== "undefined" ? localStorage.getItem("accessToken") : null;

  const url = attemptGuid ? `${BASE_API_URL}/api/v1/paper-annotations/${attemptGuid}` : null;

  // Load once, before anything is drawn, so a save cannot race the load and
  // write an empty board over what was there.
  useEffect(() => {
    if (!url || !token) return undefined;
    let live = true;
    (async () => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!live) return;
        revision.current = data.revision || 0;
        fromServer.current = true;
        onLoadedRef.current?.(data.document || {});
      } catch {
        /* a paper still works without its annotations */
      } finally {
        if (live) loaded.current = true;
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, token]);

  const save = useCallback(
    async (doc) => {
      if (!url || !token) return;
      try {
        const res = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ document: doc, base_revision: revision.current }),
        });
        if (res.status === 409) {
          // Somewhere else moved it on. Take theirs rather than clobber it.
          const fresh = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
          if (!fresh.ok) return;
          const data = await fresh.json();
          revision.current = data.revision || 0;
          fromServer.current = true;
          onLoadedRef.current?.(data.document || {});
          return;
        }
        if (!res.ok) return;
        const saved = await res.json();
        revision.current = saved.revision || revision.current;
      } catch {
        /* the next change will try again */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url, token]
  );

  /**
   * Write when the drawing pauses, and only when it has actually changed.
   *
   * The pending write is deliberately not cancelled on every re-run. The
   * board hands back a new list whenever it is touched — raising a note to
   * the front reorders it without changing a thing — so a cleanup that
   * cleared the timer would cancel a real save and then return early,
   * having found nothing new to schedule. The wait is only ever replaced by
   * a newer one, and cleared for good when the paper closes.
   */
  useEffect(() => {
    if (!enabled || !loaded.current) return;
    const signature = JSON.stringify(document);
    if (fromServer.current) {
      fromServer.current = false;
      lastSent.current = signature;
      return;
    }
    if (signature === lastSent.current) return;
    lastSent.current = signature;

    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(document), SAVE_PAUSE_MS);
  }, [document, enabled, save]);

  useEffect(() => () => clearTimeout(timer.current), []);
}
